import {
  hooks,
  Appear,
  Buttons,
  Flex,
  Icon,
  ProgressButton,
  Tab,
  Tabs,
  Text,
} from '@bitrise/bitkit';
import React, { FunctionComponent, useCallback, useContext } from 'react';
import { useParams, Route, Switch } from 'react-router-dom';

import dataAtIds from '../../../../dataAtIds.json';
import { formatter1 } from '../../../../utils/metric/getFormattedValue';
import { trackEvent } from '../../../../utils/tracking';
import SessionInteractions from '../../../SessionViewer/SessionInteractions';
import useSession from '../../../SessionViewer/useSession';
import { URLStateContext } from '../../../URLState/URLState';
import useCrashEvent from '../../useCrashEvent';
import CrashDetails from './CrashDetails';
import CrashEventAttributes from './CrashEventAttributes';

interface Params {
  appId: string;
  crashId: string;
  eventId?: string;
  tab?: string;
}

const CrashPage: FunctionComponent = () => {
  const { appId, crashId, eventId, tab } = useParams<Params>();
  const matchQuery = hooks.useMediaQuery(['1080px']);
  const { push } = useContext(URLStateContext);
  const {
    pagination,
    crashEvent,
    refetch,
    isError: isErrorCrash,
    isFetching: isFetchingCrash,
  } = useCrashEvent(crashId, eventId);
  const { metrics, session, isError: isErrorSession, isFetching: isFetchingSession } = useSession(
    /**
     * We can rely on the `app.session.id` for simulator apps once
     * https://bitrise.atlassian.net/browse/APM-2584
     * is done.
     */
    process.env.NODE_ENV === 'development'
      ? '01F092785TNFT32VVNGG45D32G'
      : crashEvent?.attributes['app.session.id'],
  );

  const handleNextCrashEventClick = useCallback(() => {
    if (pagination.next) {
      push(`/${appId}/crashes/${crashId}/event/${pagination.next}${tab ? `/${tab}` : ''}`);
    }

    trackEvent('IssueDetails.Page', { direction: 'next' });
  }, [appId, pagination.next, crashId, push, tab]);

  const handlePreviousCrashEventClick = useCallback(() => {
    if (pagination.previous) {
      push(`/${appId}/crashes/${crashId}/event/${pagination.previous}${tab ? `/${tab}` : ''}`);
    }

    trackEvent('IssueDetails.Page', { direction: 'prev' });
  }, [appId, pagination.previous, crashId, push, tab]);

  const handleViewDetails = () => {
    push(`/${appId}/crashes/${crashId}/event/${eventId}`);
    trackEvent('IssueDetails.ViewDetails', {});
  };

  const handleViewSession = () => {
    push(`/${appId}/crashes/${crashId}/event/${eventId}/session`);
    trackEvent('IssueDetails.ViewSession', {});
  };

  return (
    <>
      <Flex
        alignChildrenHorizontal="end"
        alignChildrenVertical="middle"
        direction="horizontal"
        gap="x5"
        margin="x6"
      >
        <Flex grow>
          <Tabs gap="x8">
            <Route exact path="/:appId/crashes/:crashId/event/:eventId">
              {({ match }) => (
                <Tab
                  active={!!match}
                  data-trace-at-id={dataAtIds.CrashPageTabDetails}
                  onClick={handleViewDetails}
                >
                  Details
                </Tab>
              )}
            </Route>

            <Route path="/:appId/crashes/:crashId/event/:eventId/session">
              {({ match }) => (
                <Tab
                  active={!!match}
                  data-trace-at-id={dataAtIds.CrashPageTabSession}
                  onClick={handleViewSession}
                >
                  Session
                </Tab>
              )}
            </Route>
          </Tabs>
        </Flex>

        <Flex alignChildrenVertical="middle" direction="horizontal" gap="x2">
          {pagination.total !== 0 && (
            <Text config="8" textColor="gray-7">
              {pagination.offset + 1} of {formatter1(pagination.total)} events
            </Text>
          )}

          <Flex>
            <Buttons>
              <ProgressButton
                data-trace-at-id={dataAtIds.CrashPagePrevEventButton}
                size="small"
                disabled={!pagination.previous || isFetchingCrash}
                progressing={isFetchingCrash}
                onClick={handlePreviousCrashEventClick}
                aria-label="Previous Crash Event"
              >
                <Icon name="ArrowBack" />
              </ProgressButton>

              <ProgressButton
                data-trace-at-id={dataAtIds.CrashPageNextEventButton}
                size="small"
                disabled={!pagination.next || isFetchingCrash}
                progressing={isFetchingCrash}
                onClick={handleNextCrashEventClick}
                aria-label="Next Crash Event"
              >
                <Icon name="ArrowForward" />
              </ProgressButton>
            </Buttons>
          </Flex>
        </Flex>
      </Flex>

      <Flex
        direction={matchQuery('1080px') ? 'horizontal' : 'vertical'}
        gap="x8"
        reverse={!matchQuery('1080px')}
      >
        <Flex direction="vertical" grow initial="none">
          <Switch>
            <Route exact path="/:appId/crashes/:crashId/event/:eventId">
              <CrashDetails
                crashEvent={crashEvent}
                isError={isErrorCrash}
                isFetching={isFetchingCrash}
                onRefetch={refetch}
              />
            </Route>

            <Route path="/:appId/crashes/:crashId/event/:eventId/session">
              {isErrorSession ? (
                <Flex direction="vertical" grow paddingVertical="x16">
                  <Appear animation="Fade" textColor="red-3">
                    <Flex alignChildren="middle" direction="vertical" height="100%">
                      <Flex>
                        <Flex alignChildren="middle" direction="vertical">
                          <Icon margin="x1" name="BitbotFailed" size="3rem" />
                        </Flex>
                        <Text align="middle" ellipsis>
                          Uh oh, it looks like this session isn&apos;t available right now.
                        </Text>
                        <Text align="middle" ellipsis>
                          Come back a bit later.
                        </Text>
                      </Flex>
                    </Flex>
                  </Appear>
                </Flex>
              ) : (
                <SessionInteractions
                  isFetching={isFetchingSession}
                  metrics={metrics}
                  session={session}
                />
              )}
            </Route>
          </Switch>
        </Flex>

        <Flex maxWidth={matchQuery('1080px') ? '16rem' : undefined}>
          <CrashEventAttributes
            attributes={crashEvent?.attributes}
            crashTimestamp={crashEvent?.timestamp}
            isFetching={isFetchingCrash || isFetchingSession}
            sessionDuration={session?.durationMs}
            sessionTimestamp={session?.start}
          />
        </Flex>
      </Flex>
    </>
  );
};

export default CrashPage;
